import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Divider,
  Avatar,
  Grid,
  Chip,
  CircularProgress,
  useTheme,
  IconButton,
  Paper,
  Tooltip,
  Tabs,
  Tab,
} from "@mui/material";
import {
  ArrowBack,
  Email,
  Phone,
  Person,
  Cake,
  VerifiedUser,
  GppBad,
  LocationOn,
  CardMembership,
  Image as ImageIcon,
} from "@mui/icons-material";
import Header from "../../components/Header";
import { tokens } from "../../theme";
import { useAuth } from "../../hooks/useAuth";

import "./DriverDetails.css";

const DriverDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { api } = useAuth();

  // State
  const [driver, setDriver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  // Fetch driver details
  useEffect(() => {
    const fetchDriverDetails = async () => {
      try {
        setLoading(true);

        const response = await api.get(`/driver/${id}`);

        if (response.data.success) {
          setDriver(response.data.data);
          setError(null);
        } else {
          setError(response.data.message || "Failed to load driver details");
        }
      } catch (err) {
        console.error("Error fetching driver details:", err);
        setError(
          err.response?.data?.message ||
            "An error occurred while fetching driver details"
        );
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchDriverDetails();
    }
  }, [id, api]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Loading state
  if (loading) {
    return (
      <Box className="driver-details-loading-container">
        <CircularProgress size={60} />
        <Typography variant="h6" mt={2}>
          Loading driver details...
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box className="driver-details-error-container">
        <Typography variant="h5" color="error" gutterBottom>
          {error}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<ArrowBack />}
          onClick={() => navigate("/team")}
        >
          Back to Drivers
        </Button>
      </Box>
    );
  }

  // Not found state
  if (!driver) {
    return (
      <Box className="driver-details-not-found-container">
        <Typography variant="h5" gutterBottom>
          Driver not found
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<ArrowBack />}
          onClick={() => navigate("/team")}
        >
          Back to Drivers
        </Button>
      </Box>
    );
  }

  // Convert driver.images to an array if it's not already
  const images = Array.isArray(driver.images)
    ? driver.images
    : driver.images
    ? [driver.images]
    : [];

  // Convert driver.legal to an array if it's not already
  const legalDocs = Array.isArray(driver.legal)
    ? driver.legal
    : driver.legal
    ? [driver.legal]
    : [];

  // Get profile image (first image or placeholder)
  const profileImage =
    images.length > 0 ? images[0].url || images[0] : "/rd.png";

  return (
    <Box className="driver-details-container">
      {/* Header with back button */}
      <Box className="driver-details-header">
        <Button
          variant="outlined"
          color="inherit"
          startIcon={<ArrowBack />}
          onClick={() => navigate("/team")}
          className="back-button"
        >
          Back to Drivers
        </Button>
        <Header title="Driver Details" subtitle="View driver information" />
      </Box>

      {/* Main content */}
      <Card className="driver-profile-card">
        <CardContent className="driver-profile-content">
          {/* Profile Header */}
          <Box className="profile-header">
            <Box className="profile-avatar-container">
              <Avatar
                src={profileImage}
                alt={driver.name || driver.username}
                className="profile-avatar"
              />
              <Chip
                icon={driver.isVerified ? <VerifiedUser /> : <GppBad />}
                label={driver.isVerified ? "Verified" : "Unverified"}
                color={driver.isVerified ? "success" : "warning"}
                className="verification-chip"
              />
            </Box>
            <Box className="profile-title">
              <Typography variant="h3" className="driver-name">
                {driver.name || driver.username}
              </Typography>
              <Typography variant="subtitle1" color="textSecondary">
                {driver.username}
              </Typography>
            </Box>
          </Box>

          <Divider className="profile-divider" />

          {/* Tabs for different sections */}
          <Box sx={{ width: "100%" }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              className="profile-tabs"
              textColor="secondary"
              indicatorColor="secondary"
            >
              <Tab label="Personal Info" icon={<Person />} />
              <Tab
                label="Documents & Images"
                icon={<CardMembership />}
                disabled={images.length === 0 && legalDocs.length === 0}
              />
            </Tabs>

            {/* Personal Info Tab */}
            {tabValue === 0 && (
              <Box className="tab-panel">
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Paper elevation={2} className="info-card">
                      <Box className="info-item">
                        <Person className="info-icon" />
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Full Name
                          </Typography>
                          <Typography variant="body1">
                            {driver.username || "Not provided"}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper elevation={2} className="info-card">
                      <Box className="info-item">
                        <Email className="info-icon" />
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Email
                          </Typography>
                          <Typography variant="body1">
                            {driver.email || "Not provided"}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper elevation={2} className="info-card">
                      <Box className="info-item">
                        <Phone className="info-icon" />
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Phone
                          </Typography>
                          <Typography variant="body1">
                            {driver.phone || "Not provided"}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper elevation={2} className="info-card">
                      <Box className="info-item">
                        <Cake className="info-icon" />
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary">
                            Age
                          </Typography>
                          <Typography variant="body1">
                            {driver.age || "Not provided"}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Documents & Images Tab */}
            {tabValue === 1 && (
              <Box className="tab-panel">
                {/* Profile Images Section */}
                {images.length > 0 && (
                  <Box className="section-container">
                    <Typography
                      variant="h6"
                      className="section-title"
                      gutterBottom
                    >
                      <ImageIcon className="section-icon" />
                      Profile Images
                    </Typography>
                    <Grid container spacing={2} className="images-grid">
                      {images.map((image, index) => (
                        <Grid item xs={6} sm={4} md={3} key={index}>
                          <Paper elevation={3} className="image-container">
                            <img
                              src={image.url || image}
                              alt={`Driver ${index + 1}`}
                              className="driver-image"
                            />
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                {/* Legal Documents Section */}
                {legalDocs.length > 0 && (
                  <Box className="section-container">
                    <Typography
                      variant="h6"
                      className="section-title"
                      gutterBottom
                    >
                      <CardMembership className="section-icon" />
                      Legal Documents
                    </Typography>
                    <Grid container spacing={2} className="documents-grid">
                      {legalDocs.map((doc, index) => (
                        <Grid item xs={6} sm={4} md={3} key={index}>
                          <Paper elevation={3} className="document-container">
                            <img
                              src={doc.url || doc}
                              alt={`Document ${index + 1}`}
                              className="document-image"
                            />
                            <Typography
                              variant="caption"
                              className="document-caption"
                            >
                              Document {index + 1}
                            </Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                {/* If no images or documents */}
                {images.length === 0 && legalDocs.length === 0 && (
                  <Box className="no-documents-container">
                    <Typography variant="body1" color="textSecondary">
                      No images or documents available for this driver.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DriverDetails;
